import { LitElement, html, nothing, unsafeCSS, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { when } from "@/common/directives/when";
import styles from "@/index.css?inline";
import EkoElement from "@/utilities/element";
import { createSlotChangeHandler } from "@/utilities/slot";
import style from "./menu-item.css?inline";
import "@/components/badge";
import "@/components/icon";
import "@/components/shortcut";

type SlottedElement = "eko-badge" | "eko-icon" | "eko-shortcut" | null;

@customElement("eko-menu-item")
class MenuItem extends EkoElement {
  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
  static styles = [...EkoElement.styles, unsafeCSS(styles), unsafeCSS(style)];

  private handleSlotChange = createSlotChangeHandler(this, {
    after: "afterElement",
    append: "appendElement",
    before: "beforeElement",
  });

  @property({ reflect: true, type: Boolean })
  active: boolean = false;

  @state()
  afterElement: SlottedElement = null;

  @state()
  appendElement: SlottedElement = null;

  @state()
  beforeElement: SlottedElement = null;

  @property({ reflect: true, type: Boolean })
  compact: boolean = false;

  @property({ reflect: true, type: Boolean })
  disabled = false;

  @property({ reflect: true, type: Boolean })
  download = false;

  @property({ reflect: true })
  href?: string;

  @property()
  target?: "_self" | "_blank" | "_parent" | "_top";

  @property({ reflect: true })
  theme: "default" | "danger" = "default";

  @property({ type: String, attribute: "data-turbo-confirm" }) turboConfirm?: string;
  @property({ type: String, attribute: "data-turbo-frame" }) turboFrame?: string;
  @property({ type: String, attribute: "data-turbo-method" }) turboMethod?: string;

  @property({ reflect: true })
  type?: "button" | "reset" | "submit";

  private renderButton(content: TemplateResult) {
    return html`
      <button
        class="eko-menu-item"
        part="root"
        data-turbo-confirm=${when(this.turboConfirm)}
        data-turbo-frame=${when(this.turboFrame)}
        data-turbo-method=${when(this.turboMethod)}
        ?disabled=${this.disabled}
        ?download=${this.download}
        type=${ifDefined(this.type)}>
        ${content}
      </button>
    `;
  }

  private renderLink(content: TemplateResult) {
    return html`
      <a
        class="eko-menu-item"
        part="root"
        href=${when(this.disabled, nothing, this.href)}
        rel=${when(this.target === "_blank", "noopener noreferrer")}
        target=${when(this.target)}
        data-turbo-confirm=${when(this.turboConfirm)}
        data-turbo-frame=${when(this.turboFrame)}
        data-turbo-method=${when(this.turboMethod)}
        ?download=${this.download}>
        ${content}
      </a>
    `;
  }

  render() {
    const content = html`
      <slot name="before" @slotchange=${this.handleSlotChange}></slot>
      <span class="eko-menu-item__content" part="content">
        <span
          class="eko-menu-item__text ${this.appendElement && "eko-menu-item__text--with-append"}"
          part="text">
          <slot @slotchange=${this.handleSlotChange}></slot>
        </span>
        <span class="eko-menu-item__append" part="append">
          <slot name="append" @slotchange=${this.handleSlotChange}></slot>
        </span>
      </span>
      <slot name="after" @slotchange=${this.handleSlotChange}></slot>
    `;

    return this.href ? this.renderLink(content) : this.renderButton(content);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-menu-item": MenuItem;
  }
}

export default MenuItem;
